import {createElement} from 'react'
import {CheckSpinner} from '@github-ui/mergebox/check-spinner'
import {
  AlertIcon,
  CheckIcon,
  GitMergeIcon,
  StopIcon,
  SkipIcon,
  GitMergeQueueIcon,
  GitPullRequestIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  DotFillIcon,
  ClockIcon,
  ArchiveIcon,
} from '@primer/octicons-react'
import type {ArtifactState} from '../types/artifact'
import type {SessionState} from '../types/session'
import {isFeatureEnabled} from '@github-ui/feature-flags'

// Defined once at module scope so the icon reference is stable across renders.
// Returning a fresh arrow function here would make React treat it as a new
// component type on every render, remounting the spinner and restarting its
// animation.
// eslint-disable-next-line @github-ui/github-monorepo/no-react-create-element -- Convert to JSX Syntax. This Requires changing to a TSX file
const AnimatedCheckSpinner = () => createElement(CheckSpinner, {animated: true})

export interface IconAttributes {
  icon: React.ComponentType | (() => React.ReactElement)
  color: string
  'data-testid': string
  marginTop?: string
}

export function getLeadingVisualIconAttributes(
  prState?: ArtifactState,
  prInMergeQueue: boolean = false,
  state?: SessionState,
  isArchived?: boolean,
): IconAttributes {
  const sessionFiltersEnabled = isFeatureEnabled('copilot_mission_control_session_filters')

  // Check PR states first
  if (prState === 'merged') {
    return {
      icon: GitMergeIcon,
      color: 'var(--fgColor-done)' as const,
      'data-testid': 'icon-git-merge',
    }
  }

  if (prState === 'closed') {
    return {
      icon: GitPullRequestClosedIcon,
      color: 'var(--fgColor-closed)' as const,
      'data-testid': 'icon-git-pull-request-closed',
    }
  }

  if (prInMergeQueue) {
    return {
      icon: GitMergeQueueIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-git-merge-queue',
    }
  }

  // Check if the task is archived next to not mask the PR state
  if (isArchived) {
    return {
      icon: ArchiveIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-archive',
    }
  }

  if (state === 'in_progress') {
    return {
      icon: AnimatedCheckSpinner,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-spinning-circle',
      marginTop: '3px',
    }
  }

  if (state === 'queued') {
    return {
      icon: ClockIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-clock',
    }
  }

  // `waiting_for_user` is a paused state — distinct from `completed`. Show
  // an alert icon in attention color so users can immediately spot
  // sessions that need their input on the Active/Completed list views.
  if (state === 'waiting_for_user') {
    return {
      icon: AlertIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-alert',
    }
  }

  if (prState === 'draft') {
    return {
      icon: GitPullRequestDraftIcon,
      color: 'var(--fgColor-draft)' as const,
      'data-testid': 'icon-git-pull-request-draft',
    }
  }

  if (prState === 'open') {
    return {
      icon: GitPullRequestIcon,
      color: 'var(--fgColor-open)' as const,
      'data-testid': 'icon-git-pull-request',
    }
  }

  if (state === 'completed') {
    return {
      icon: CheckIcon,
      color: 'var(--fgColor-success)' as const,
      'data-testid': 'icon-check',
    }
  }

  if (state === 'failed' || state === 'timed_out') {
    return {
      icon: StopIcon,
      color: 'var(--fgColor-danger)' as const,
      'data-testid': 'icon-stop',
    }
  }

  if (state === 'idle') {
    if (sessionFiltersEnabled) {
      return {
        icon: CheckIcon,
        color: 'var(--fgColor-success)' as const,
        'data-testid': 'icon-check',
      }
    }
    return {
      icon: DotFillIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-dot',
    }
  }

  if (state === 'cancelled') {
    return {
      icon: SkipIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-skip',
    }
  }

  return {
    icon: DotFillIcon,
    color: 'var(--fgColor-muted)' as const,
    'data-testid': 'icon-idle',
  }
}
